import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface KpiDetailItem {
  label: string;
  value: string | number;
  color?: string;
}

interface KpiCardProps {
  label: string;
  value: string | number;
  change?: number;
  icon: string;
  suffix?: string;
  prefix?: string;
  navigateTo?: string;
  detail?: {
    description: string;
    items: KpiDetailItem[];
    trend?: string;
  };
}

const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  change,
  icon,
  suffix,
  prefix,
  navigateTo,
  detail,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();
  const isPositive = (change ?? 0) >= 0;
  const isClickable = !!(navigateTo || detail);

  const handleClick = () => {
    if (detail) {
      setShowPopup(true);
    } else if (navigateTo) {
      navigate(navigateTo);
    }
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (navigateTo) navigate(navigateTo);
  };

  return (
    <>
      <div
        className={`relative rounded-xl p-4 flex flex-col gap-2 transition-all duration-300 hover:-translate-y-1 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
        style={{
          background: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(12px)',
          border: showPopup
            ? '1px solid rgba(255,107,71,0.5)'
            : '1px solid rgba(255,255,255,0.08)',
          minWidth: '180px',
          boxShadow: showPopup ? '0 0 18px rgba(255,77,77,0.18)' : undefined,
        }}
        onClick={handleClick}
      >
        <div
          className="absolute inset-0 rounded-xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255,77,77,0.05) 0%, transparent 100%)',
          }}
        />
        <div className="flex items-start justify-between relative z-10">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, rgba(255,77,77,0.25), rgba(255,140,66,0.15))' }}
          >
            <i className={`${icon} text-sm`} style={{ color: '#ff6b47' }}></i>
          </div>
          <div className="flex items-center gap-1.5">
            {change !== undefined && (
              <span
                className="text-xs font-medium flex items-center gap-0.5"
                style={{ color: isPositive ? '#10b981' : '#ef4444' }}
              >
                <i className={`${isPositive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-xs`}></i>
                {Math.abs(change)}%
              </span>
            )}
            {isClickable && (
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-information-line text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}></i>
              </div>
            )}
          </div>
        </div>
        <div className="relative z-10">
          <div
            className="text-2xl font-bold text-white leading-tight"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            {prefix}{typeof value === 'number' ? value.toLocaleString('tr-TR') : value}{suffix}
          </div>
          <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{label}</div>
        </div>
        {isClickable && (
          <div
            className="absolute bottom-2 right-2 opacity-0 hover:opacity-100 transition-opacity"
            style={{ color: '#ff6b47' }}
          >
            <i className="ri-arrow-right-line text-xs"></i>
          </div>
        )}
      </div>

      {/* Detail Popup */}
      {showPopup && detail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowPopup(false)}
        >
          <div
            className="relative rounded-2xl p-6 w-full max-w-sm mx-4 animate-fade-in"
            style={{
              background: 'rgba(18,18,24,0.98)',
              border: '1px solid rgba(255,107,71,0.25)',
              boxShadow: '0 0 40px rgba(255,77,77,0.15), 0 24px 60px rgba(0,0,0,0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Glow */}
            <div
              className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{ background: 'linear-gradient(135deg, rgba(255,77,77,0.06) 0%, transparent 60%)' }}
            />

            {/* Header */}
            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(255,77,77,0.3), rgba(255,140,66,0.2))' }}
                >
                  <i className={`${icon} text-base`} style={{ color: '#ff6b47' }}></i>
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{label}</div>
                  <div className="text-xs" style={{ color: '#6b7280' }}>{detail.description}</div>
                </div>
              </div>
              <button
                onClick={() => setShowPopup(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg transition-all hover:bg-white/10"
                style={{ color: '#6b7280' }}
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>

            {/* Main Value */}
            <div
              className="rounded-xl p-4 mb-4 text-center relative z-10"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div
                className="text-4xl font-bold"
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  background: 'linear-gradient(135deg, #ff4d4d, #ff8c42)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {prefix}{typeof value === 'number' ? value.toLocaleString('tr-TR') : value}{suffix}
              </div>
              {change !== undefined && (
                <div
                  className="text-xs mt-1 flex items-center justify-center gap-1"
                  style={{ color: isPositive ? '#10b981' : '#ef4444' }}
                >
                  <i className={`${isPositive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-xs`}></i>
                  Dünden {isPositive ? 'artış' : 'düşüş'}: %{Math.abs(change)}
                </div>
              )}
              {detail.trend && (
                <div className="text-xs mt-1" style={{ color: '#6b7280' }}>{detail.trend}</div>
              )}
            </div>

            {/* Detail Items */}
            <div className="space-y-2 mb-4 relative z-10">
              {detail.items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between px-3 py-2 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                >
                  <span className="text-xs" style={{ color: '#9ca3af' }}>{item.label}</span>
                  <span
                    className="text-xs font-semibold"
                    style={{ color: item.color ?? '#e5e7eb', fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Navigate Button */}
            {navigateTo && (
              <button
                onClick={handleNavigate}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 relative z-10 flex items-center justify-center gap-2 whitespace-nowrap"
                style={{
                  background: 'linear-gradient(135deg, #ff4d4d, #ff8c42)',
                  boxShadow: '0 4px 16px rgba(255,77,77,0.3)',
                }}
              >
                Detaylı Görünüme Git
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default KpiCard;
